import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { map, mergeMap, of } from 'rxjs'

import { AtomModule } from '../../atom/atom.module'
import { AppService } from '../../service/app.service'
import { CustomerService } from '../../service/customer.service';
import { CartService } from '../../service/cart.service';
import { PaymentService } from '../../service/payment.service';
import { NzNotificationService } from 'ng-zorro-antd/notification'

interface StripeConfig {
  scriptUrl: string;
  publishableKey: string;
  options: JSON;
}

@Component({
  selector: 'app-payment-stripe',
  templateUrl: './payment-stripe.component.html',
  styleUrl: './payment-stripe.component.less',
  standalone: true,
  imports: [AtomModule],
})
export class PaymentStripeComponent implements OnInit {
  
  constructor(
    private appService: AppService,
    private nzNotificationService: NzNotificationService,
    private customerService: CustomerService,
    private cardService: CartService,
    private paymentService: PaymentService
  ) {}

  @Input() config: StripeConfig;
  
  isBrowser: boolean
  paymentIntent: string;
  orderId: string;
  isStripeLoading = true
  isStripeLoadedError = false;
  stripe: any;
  elements: any;

  ngOnInit() {
    this.appService.isBrowser$.subscribe((state) => {
      this.isBrowser = state
      
      if (this.isBrowser) {
        console.log(`this.config: ${this.config}`)
        if (this.config) {
          this.loadStripeScript(this.config.scriptUrl)
        }
      }
    })
  }

  loadStripeScript(scriptUrl: string) {
    // Prevent duplicate script loading
    if (document.querySelector(`script[src="${scriptUrl}"]`)) {
      console.log('Stripe script already loaded');
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      console.log('Stripe script loaded');
      this.initializeStripe();
    };
    script.onerror = () => {
      console.error('Error loading Stripe script');
    };

    document.body.appendChild(script);
  }
  
  initializeStripe() {
    if (!(window as any).Stripe) {
      console.error('Stripe not available');
      return;
    }

    this.customerService.getActiveCustomer().pipe(
      mergeMap((activeCustomerResponse) => {
        return this.cardService.fetchCartItems().pipe(
          map((cartItemResponse) => ({ activeCustomerResponse, cartItemResponse }))
        )
      }),
      mergeMap(({ activeCustomerResponse, cartItemResponse }) => {
        const customerId = activeCustomerResponse.data.activeCustomer?.id;
        const orderId = cartItemResponse.data.activeOrder?.id;
        this.orderId = orderId as string
        return this.paymentService.createPaymentIntent({
          agreementAccepted: true,
          customerId,
          orderId,
        })
      })
    ).subscribe((paymentIntentResponse) => {      
      const paymentIntent = paymentIntentResponse.data?.createPaymentIntent;
      
      if (!paymentIntent) {
        this.isStripeLoadedError = true
        this.nzNotificationService.error("Stripe Initialization Error", "Payment intent not found")
        return
      }
      
      if (!paymentIntent?.includes("secret")) { 
        this.isStripeLoadedError = true
        this.nzNotificationService.error("Stripe Initialization Error", paymentIntent)
        return
      }
      
      this.paymentIntent = paymentIntent;
      
      this.stripe = (window as any).Stripe(this.config.publishableKey);
      
      // Fetches a payment intent and captures the client secret
      const appearance = {
        theme: 'stripe',
      };
  
      const paymentElementOptions = {
        layout: "accordion",
      };
      
      this.elements = this.stripe.elements({ appearance, clientSecret: paymentIntent});
  
      const paymentElement = this.elements.create('payment', paymentElementOptions);
      paymentElement.mount('#payment-element');
  
      paymentElement.on('change', (event: any) => {
        this.updatePaymentIntent(event);
      });
      
      this.isStripeLoadedError = false
      this.isStripeLoading = false;
    })
  }
  
  extractPublicKey(key: string): string {
    return key.split("_secret_")[0];
  }

  updatePaymentIntent(event: any) {
    const selectedType = event.value?.type

    if(!selectedType){
      return
    }
    
    const supportsSetupFutureUsage = ["card"]

    const shouldSaveForFutureUse = supportsSetupFutureUsage.includes(selectedType)
    console.log(`shouldSaveForFutureUse: ${shouldSaveForFutureUse}  selectedType: ${selectedType}`)

    this.paymentService.updatePaymentIntent({
      paymentIntentId: this.extractPublicKey(this.paymentIntent),
      shouldSaveForFutureUse: shouldSaveForFutureUse
    }).subscribe()
  }
  
  async confirmPayment() { 
    await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: window.location.origin + '/payment-complete?order_id=' + this.orderId,
      },
    });
  }
}
